<template>
  <div id="map" class="map" :style="{ height: height }"></div>
</template>

<script>
import loadGoogleMapsApi from "load-google-maps-api";

export default {
  name: "MapDirections",
  props: {
    start: Object,
    end: Object,
    height: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      map: null,
      directionsService: null,
      directionsRenderer: null,
      polyline: null,
      startMarker: null,
      endMarker: null,
      isLoading: false,
      fullPage: false,
    };
  },
  created() {
    loadGoogleMapsApi({
      key: process.env.VUE_APP_GOOGLE_API_KEY,
      libraries: ["places", "drawing", "geometry"],
    }).then(() => {
      this.initMap();
    });
  },
  methods: {
    initMap() {
      const { google } = window;
      this.map = new google.maps.Map(document.getElementById("map"), {
        zoom: 4,
        center: this.start || { lat: 51.5074, lng: -0.1278 }, // Default to London if start is not provided
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
      });

      this.directionsService = new google.maps.DirectionsService();
      this.directionsRenderer = new google.maps.DirectionsRenderer({
        suppressMarkers: true,
      });
      this.directionsRenderer.setMap(this.map);

      this.polyline = new google.maps.Polyline({
        path: [],
        geodesic: true,
        strokeColor: "#4285F4",
        strokeOpacity: 0.6,
        strokeWeight: 6,
      });

      this.startMarker = new google.maps.Marker({
        map: this.map,
        title: "Point A",
        label: "A",
      });

      this.endMarker = new google.maps.Marker({
        map: this.map,
        title: "Point B",
        label: "B",
      });

      if (this.start && this.end) {
        this.calculateAndDisplayRoute();
      }
    },
    resetMap() {
      if (this.directionsRenderer) {
        this.directionsRenderer.setDirections({ routes: [] });
      }

      if (this.polyline) {
        this.polyline.setMap(null);
      }

      if (this.startMarker) this.startMarker.setMap(null);
      if (this.endMarker) this.endMarker.setMap(null);
    },
    calculateAndDisplayRoute() {
      const start = this.start;
      const end = this.end;

      if (!this.map || !this.directionsRenderer) return;

      this.$emit("isLoading", true);

      this.resetMap();

      this.directionsService.route(
        {
          origin: start,
          destination: end,
          travelMode: "DRIVING",
        },
        (response, status) => {
          if (status === "OK") {
            this.directionsRenderer.setDirections(response);
            const route = response.routes[0].legs[0];
            this.startMarker.setPosition(route.start_location);
            this.endMarker.setPosition(route.end_location);
            this.startMarker.setMap(this.map);
            this.endMarker.setMap(this.map);
          } else if (status === "ZERO_RESULTS") {
            console.error(
              "No routes found for DRIVING. Falling back to polylines..."
            );
            this.drawPolylineFallback();
          } else {
            console.error("Directions request failed due to " + status);
          }
          this.$emit("isLoading", false);
        }
      );
    },
    drawPolylineFallback() {
      const routePath = [this.start, this.end];
      this.polyline.setPath(routePath);
      this.polyline.setMap(this.map);

      this.startMarker.setPosition(this.start);
      this.endMarker.setPosition(this.end);

      this.startMarker.setMap(this.map);
      this.endMarker.setMap(this.map);

      const bounds = new google.maps.LatLngBounds();
      routePath.forEach((point) => bounds.extend(point));
      this.map.fitBounds(bounds);

      this.$emit("isLoading", false);
    },
  },
  watch: {
    startEnd: {
      handler(val) {
        if (
          val &&
          val.start &&
          val.end &&
          this.map &&
          this.directionsRenderer
        ) {
          this.calculateAndDisplayRoute();
        }
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    startEnd() {
      return {
        start: this.start,
        end: this.end,
      };
    },
  },
};
</script>

<style scoped>
.map {
  height: 100%;
  border-radius: 8px;
}

@media (max-width: 767px) {
  .map {
    height: 250px;
  }
}
</style>
